import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { openPdf } from "@/lib/pdf";
import { Plus, Trash2, Search, Gift, Ticket as TicketIcon, Mail, Copy, Download, X, CreditCard, RotateCcw, Printer } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  { v: "geld", l: "Geldgutschein", icon: Gift },
  { v: "ticket_pack", l: "10er-Karte", icon: TicketIcon },
  { v: "ticket", l: "Freikarte", icon: TicketIcon },
  { v: "popcorn", l: "Popcorn", icon: Gift },
  { v: "menu", l: "Menü", icon: Gift },
  { v: "vip", l: "VIP", icon: Gift },
  { v: "geburtstag", l: "Geburtstag", icon: Gift },
];
const THEMES = { classic: "Klassisch", christmas: "Weihnachten", movie: "Lieblingsfilm", cinema_ticket: "Cinema Ticket" };
const kindMeta = (k) => KINDS.find((x) => x.v === k) || { l: k, icon: Gift };

export default function Vouchers() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ kind: "geld", value: 25, usage_limit: 1, valid_until: "" });
  const [query, setQuery] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/vouchers").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/vouchers", form);
      toast.success("Gutschein erstellt");
      setForm({ kind: "geld", value: 25, usage_limit: 1, valid_until: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Löschen?")) return; await api.delete(`/vouchers/${id}`); load(); };
  const stripeRefund = async (v) => {
    if (!window.confirm(`Stripe-Refund für Code ${v.code} auslösen?\n\nErstattet ${(v.value || 0).toFixed(2)} € auf die Original-Zahlungsmethode und storniert den Gutschein unwiderruflich.`)) return;
    try {
      const { data } = await api.post(`/vouchers/${v.id}/refund`);
      toast.success(`Refund ${data.refund_id?.slice(0, 12)}… über ${data.amount?.toFixed(2)} € erfolgreich`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Stripe-Refund fehlgeschlagen");
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((v) => {
      if (kindFilter && v.kind !== kindFilter) return false;
      if (statusFilter && (v.status || "aktiv") !== statusFilter) return false;
      if (!q) return true;
      return (v.code || "").toLowerCase().includes(q)
        || (v.recipient_name || "").toLowerCase().includes(q)
        || (v.from_name || "").toLowerCase().includes(q)
        || (v.buyer_email || "").toLowerCase().includes(q);
    });
  }, [rows, query, kindFilter, statusFilter]);

  const stats = useMemo(() => {
    const active = rows.filter((v) => (v.status || "aktiv") === "aktiv");
    const sum = active.reduce((s, v) => s + (v.remaining_value || 0), 0);
    const packs = rows.filter((v) => v.kind === "ticket_pack");
    const packUses = packs.reduce((s, v) => s + (v.ticket_uses_remaining || 0), 0);
    return { total: rows.length, active: active.length, outstanding: sum, packUses };
  }, [rows]);

  return (
    <div className="space-y-6" data-testid="vouchers-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Geschenke</div>
        <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl">Gutscheine &amp; Kinokarten</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Gesamt" value={stats.total} sub="Ausgegebene" />
        <Stat label="Aktiv" value={stats.active} sub="Noch einlösbar" />
        <Stat label="Offener Wert" value={`${stats.outstanding.toFixed(2)} €`} sub="Auf allen Karten" gold />
        <Stat label="Offene Eintritte" value={stats.packUses} sub="In 10er-Karten" />
      </div>

      <div className="card-lux p-4 grid md:grid-cols-6 gap-3 items-end">
        <div className="md:col-span-1">
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Typ</label>
          <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none">
            {KINDS.filter((k) => k.v !== "ticket_pack").map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
          </select>
        </div>
        <F label="Wert (€)" v={form.value} on={(v) => setForm({ ...form, value: parseFloat(v || 0) })} type="number" />
        <F label="Nutzungslimit" v={form.usage_limit} on={(v) => setForm({ ...form, usage_limit: parseInt(v || 1) })} type="number" />
        <F label="Gültig bis" v={form.valid_until} on={(v) => setForm({ ...form, valid_until: v })} type="date" />
        <button data-testid="voucher-create-btn" onClick={create}
          className="md:col-span-2 bg-[#D4AF37] text-black px-4 py-2 rounded-md text-sm uppercase tracking-widest hover:bg-[#EEDC82] flex items-center justify-center gap-2">
          <Plus size={14} /> Intern erstellen
        </button>
      </div>

      <div className="card-lux p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#575148]" size={14} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Code, Empfänger, E-Mail …" data-testid="voucher-search"
            className="w-full pl-9 pr-3 py-2 bg-[#050505] border border-[#2A2A2A] rounded text-sm outline-none focus:border-[#D4AF37]" />
        </div>
        <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}
          className="bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm outline-none focus:border-[#D4AF37]" data-testid="voucher-filter-kind">
          <option value="">Alle Typen</option>
          {KINDS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm outline-none focus:border-[#D4AF37]" data-testid="voucher-filter-status">
          <option value="">Alle Status</option>
          <option value="aktiv">Aktiv</option>
          <option value="verbraucht">Verbraucht</option>
          <option value="storniert">Storniert</option>
        </select>
        <div className="text-xs text-[#A8A398]">{filtered.length} von {rows.length}</div>
      </div>

      <div className="card-lux overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-[#0A0A0A] border-b border-[#1A1A1A] text-[#A8A398] text-[10px] uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Code</th>
              <th className="text-left p-3">Typ</th>
              <th className="text-left p-3">Empfänger / Käufer</th>
              <th className="text-right p-3">Wert</th>
              <th className="text-right p-3">Rest</th>
              <th className="text-center p-3">Uses</th>
              <th className="text-left p-3">Gültig bis</th>
              <th className="text-left p-3">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((v) => {
              const meta = kindMeta(v.kind);
              const Icon = meta.icon;
              const pack = v.kind === "ticket_pack";
              const usedUp = (v.status || "aktiv") !== "aktiv";
              return (
                <tr key={v.id} className={`border-b border-[#141414] cursor-pointer hover:bg-[#0F0D08] ${usedUp ? "opacity-60" : ""}`}
                  onClick={() => setDetail(v)} data-testid={`voucher-row-${v.id}`}>
                  <td className="p-3 font-mono text-[#D4AF37]">{v.code}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <Icon size={13} className="text-[#A8A398]" />
                      <span>{meta.l}</span>
                      {v.theme && v.theme !== "classic" && (
                        <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#D4AF37]/15 text-[#D4AF37]">
                          {THEMES[v.theme] || v.theme}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    {v.recipient_name && <div className="text-[#F5F0E6]">{v.recipient_name}</div>}
                    {v.buyer_email && <div className="text-xs text-[#A8A398]">{v.buyer_email}</div>}
                    {!v.recipient_name && !v.buyer_email && <span className="text-[#575148]">—</span>}
                    {v.from_name && <div className="text-[9px] uppercase tracking-widest text-[#575148] mt-0.5">Von {v.from_name}</div>}
                    {v.paid_online && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-[#635BFF] bg-[#635BFF]/10 border border-[#635BFF]/40 rounded px-1.5 py-0.5" data-testid={`voucher-paid-online-${v.id}`}>
                        <CreditCard size={9} /> Stripe {v.stripe_refund_id ? "· erstattet" : ""}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-right font-mono">{(v.value || 0).toFixed(2)} €</td>
                  <td className="p-3 text-right font-mono">
                    <span className={v.remaining_value === v.value ? "text-[#F5F0E6]" : "text-[#D4AF37]"}>
                      {(v.remaining_value || 0).toFixed(2)} €
                    </span>
                  </td>
                  <td className="p-3 text-center font-mono">
                    {pack ? `${v.ticket_uses_remaining ?? 0}/${v.ticket_count ?? "?"}` : "—"}
                  </td>
                  <td className="p-3 text-[#A8A398] text-xs">{v.valid_until || "—"}</td>
                  <td className="p-3">
                    <span className={
                      v.status === "verbraucht" ? "text-[#575148] uppercase text-[10px] tracking-widest"
                        : v.status === "storniert" ? "text-[#EE9CA1] uppercase text-[10px] tracking-widest"
                        : "text-[#9BE0A5] uppercase text-[10px] tracking-widest"
                    }>{v.status || "aktiv"}</span>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/vouchers/${v.id}/pdf`} target="_blank" rel="noreferrer"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); openPdf(`/vouchers/${v.id}/pdf`); }} title="Als PDF öffnen"
                        className="text-xs px-2 py-1 border border-[#D4AF37]/40 text-[#D4AF37] rounded hover:bg-[#D4AF37] hover:text-black mr-2 inline-flex items-center gap-1" data-testid={`voucher-pdf-${v.id}`}>
                        <Download size={12} /> PDF
                      </a>
                      <button onClick={async (e) => {
                          e.stopPropagation();
                          try { const { data } = await api.post(`/vouchers/${v.id}/print-office`); toast.success(`Gedruckt (${data.printer})`); }
                          catch (err) {
                            toast.info("Bürodrucker nicht erreichbar — PDF wird geöffnet");
                            openPdf(`/vouchers/${v.id}/pdf`);
                          }
                        }} title="Über Bürodrucker drucken (öffnet PDF wenn Drucker offline)"
                        className="text-xs px-2 py-1 border border-[#8FBC8F]/60 text-[#8FBC8F] rounded hover:bg-[#8FBC8F]/10 mr-2 inline-flex items-center gap-1" data-testid={`voucher-print-${v.id}`}>
                        <Printer size={12} /> Druck
                      </button>
                    {v.paid_online && !v.stripe_refund_id && v.status !== "storniert" && (
                      <button onClick={(e) => { e.stopPropagation(); stripeRefund(v); }}
                        title="Stripe-Refund + Gutschein stornieren"
                        className="text-xs px-2 py-1 border border-[#635BFF]/60 text-[#9C97FF] rounded hover:bg-[#635BFF] hover:text-white mr-2 inline-flex items-center gap-1" data-testid={`voucher-refund-${v.id}`}>
                        <RotateCcw size={12} /> Refund
                      </button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); remove(v.id); }}
                      className="text-xs px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#7A1921]" data-testid={`voucher-delete-${v.id}`}>
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {!filtered.length && (
              <tr><td colSpan={9} className="p-8 text-center text-[#A8A398]">Keine Gutscheine</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      {detail && <VoucherDetail v={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function VoucherDetail({ v, onClose }) {
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  const meta = kindMeta(v.kind);
  const pack = v.kind === "ticket_pack";
  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Kopiert"); };
  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="voucher-detail-modal">
      <div className="relative w-full max-w-lg bg-[#0F0D08] border border-[#D4AF37]/40 rounded-lg p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-[#F5F0E6]/70 hover:text-[#D4AF37]"><X size={22} /></button>

        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">
          <meta.icon size={14} /> {meta.l}
          {v.theme && v.theme !== "classic" && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#D4AF37]/15">{THEMES[v.theme] || v.theme}</span>
          )}
        </div>
        <div className="font-heading text-2xl sm:text-3xl md:text-4xl text-[#D4AF37] mt-2">
          {pack ? `${v.ticket_count ?? "?"}er-Karte` : `${(v.value || 0).toFixed(2)} €`}
        </div>

        <div className="mt-4 flex items-center gap-2 p-3 border border-[#D4AF37]/40 bg-[#D4AF37]/[0.05] rounded">
          <div className="font-mono text-lg text-[#D4AF37] flex-1">{v.code}</div>
          <button onClick={() => copy(v.code)} className="text-xs px-3 py-1 border border-[#2A2A2A] rounded hover:border-[#D4AF37] flex items-center gap-1" data-testid="voucher-copy-code">
            <Copy size={12} /> Kopieren
          </button>
        </div>

        <div className="mt-6 space-y-3 text-sm">
          <Row label="Empfänger" value={v.recipient_name} />
          <Row label="Von" value={v.from_name} />
          <Row label="Käufer-Mail" value={v.buyer_email} icon={v.buyer_email ? <Mail size={12} /> : null} />
          <Row label="Wert" value={`${(v.value || 0).toFixed(2)} €`} mono />
          <Row label="Restwert" value={`${(v.remaining_value || 0).toFixed(2)} €`} mono highlight />
          {pack && <Row label="Verbleibende Eintritte" value={`${v.ticket_uses_remaining ?? 0} / ${v.ticket_count ?? "?"}`} mono highlight />}
          {pack && v.price_per_ticket && <Row label="Preis pro Ticket" value={`${v.price_per_ticket.toFixed(2)} €`} mono />}
          <Row label="Gültig bis" value={v.valid_until} />
          <Row label="Bestellt am" value={v.created_at ? new Date(v.created_at).toLocaleDateString("de-DE") + " " + new Date(v.created_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : "—"} />
          <Row label="Status" value={v.status || "aktiv"} />
        </div>

        {v.message && (
          <div className="mt-5 p-4 border-l-3 border-l-[#D4AF37] bg-[#0F0D08]" data-testid="voucher-message">
            <div className="text-[10px] uppercase tracking-widest text-[#A8A398] mb-1">Persönliche Nachricht</div>
            <div className="italic text-[#F5F0E6] text-sm">&bdquo;{v.message}&ldquo;</div>
          </div>
        )}

        {v.notes && (
          <div className="mt-4 text-xs text-[#A8A398]">
            <span className="uppercase tracking-widest text-[#575148]">Notiz:</span> {v.notes}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, mono, highlight, icon }) {
  if (!value) return null;
  return (
    <div className="flex justify-between items-center gap-4 border-b border-[#1A1A1A] pb-2">
      <div className="text-[10px] uppercase tracking-widest text-[#A8A398] flex items-center gap-1">{icon}{label}</div>
      <div className={`${mono ? "font-mono" : ""} ${highlight ? "text-[#D4AF37]" : "text-[#F5F0E6]"} text-right`}>{value}</div>
    </div>
  );
}

function Stat({ label, value, sub, gold }) {
  return (
    <div className="card-lux p-4">
      <div className="text-[9px] uppercase tracking-[0.35em] text-[#A8A398]">{label}</div>
      <div className={`font-heading text-3xl mt-1 ${gold ? "text-[#D4AF37]" : "text-[#F5F0E6]"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#575148] mt-1">{sub}</div>
    </div>
  );
}

function F({ label, v, on, type = "text" }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">{label}</label>
      <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)}
        className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
    </div>
  );
}
